import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import Markdown from 'react-markdown';
import { Link } from 'react-router';
import remarkGfm from 'remark-gfm';

import type {
  ApartmentContactRole,
  ApartmentContactView,
  ApartmentDocumentKind,
  ApartmentDocumentView,
  ApartmentPaymentKind,
  ApartmentPaymentView,
  ApartmentView,
  UtilityBillView,
} from '../../../shared/api.js';
import { api } from '../lib/api';

/** Every role, kind and type must be here (the type checks it). */
const ROLE_LABELS: Record<ApartmentContactRole, string> = {
  manager: 'Администратор',
  community: 'Сообщество собственников',
  neighbor: 'Сосед',
  utility: 'Коммунальные услуги',
  repair: 'Ремонт',
  cleaning: 'Уборка',
  agent: 'Агент',
  tenant: 'Жилец',
  other: 'Другое',
};
const PAYMENT_LABELS: Record<ApartmentPaymentKind, string> = {
  utilities: 'Свет, вода, газ',
  internet: 'Интернет и телефон',
  community: 'Взносы сообщества',
  tax: 'Налоги',
  mortgage: 'Ипотека',
  rent: 'Аренда',
  insurance: 'Страховка',
  repair: 'Ремонт',
  other: 'Другое',
};
const DOCUMENT_LABELS: Record<ApartmentDocumentKind, string> = {
  deed: 'Право собственности',
  contract: 'Договор',
  insurance: 'Страховка',
  utility: 'Договор на услуги',
  invoice: 'Счёт',
  tax: 'Налоги',
  certificate: 'Сертификат',
  other: 'Другое',
};
const FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.txt,.md,.doc,.docx,.xls,.xlsx,.zip';

const options = <K extends string>(labels: Record<K, string>) =>
  (Object.keys(labels) as K[]).map((k) => (
    <option key={k} value={k}>
      {labels[k]}
    </option>
  ));

const today = () => new Date().toISOString().slice(0, 10);

function formatDay(day: string): string {
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium' }).format(date);
}

function formatAmount(amount: string, currency: string): string {
  const value = Number(amount);
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).format(value);
  } catch {
    return `${amount} ${currency}`;
  }
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['apartments'] });
}

function FormButtons({
  pending,
  ready = true,
  label,
  onCancel,
}: {
  pending: boolean;
  ready?: boolean;
  label: string;
  onCancel: () => void;
}) {
  return (
    <div className="actions-row">
      <button type="submit" className="btn btn--primary" disabled={!ready || pending}>
        {pending ? 'Сохраняю…' : label}
      </button>
      <button type="button" className="btn btn--ghost" onClick={onCancel}>
        Отмена
      </button>
    </div>
  );
}

/** A new apartment: the slug names it for the agent's tools. */
function AddApartmentForm({
  onAdded,
  onCancel,
}: {
  onAdded: (slug: string) => void;
  onCancel: () => void;
}) {
  const invalidate = useInvalidate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('ES');
  const [currency, setCurrency] = useState('EUR');
  const [address, setAddress] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api.addApartment({
        slug: slug.trim().toLowerCase(),
        name: name.trim(),
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(country.trim() ? { country: country.trim().toUpperCase() } : {}),
        ...(currency.trim() ? { currency: currency.trim().toUpperCase() } : {}),
        ...(address.trim() ? { address: address.trim() } : {}),
      }),
    onSuccess: () => {
      void invalidate();
      onAdded(slug.trim().toLowerCase());
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate();
  };
  return (
    <form className="card form form--wide" onSubmit={submit}>
      <div className="card__header">
        <h3>Новая квартира</h3>
      </div>
      <div className="form__row">
        <label>
          Название
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
            placeholder="Sitges"
          />
        </label>
        <label>
          Короткое имя <span className="muted small">(латиница, для агента)</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            maxLength={40}
            pattern="[a-zA-Z0-9][a-zA-Z0-9\-]*"
            placeholder="sitges"
          />
        </label>
      </div>
      <div className="form__row">
        <label>
          Город
          <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={200} />
        </label>
        <label>
          Страна
          <input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} />
        </label>
        <label>
          Валюта
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
        </label>
      </div>
      <label>
        Адрес
        <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
      </label>
      {add.isError && <p className="text-error small">{add.error.message}</p>}
      <FormButtons
        pending={add.isPending}
        ready={name.trim().length > 0 && slug.trim().length > 0}
        label="Добавить квартиру"
        onCancel={onCancel}
      />
    </form>
  );
}

/** Name, address, access and notes, changed in place; the apartment deleted from here too. */
function InfoSection({
  apartment,
  canWrite,
  onDeleted,
}: {
  apartment: ApartmentView;
  canWrite: boolean;
  onDeleted: () => void;
}) {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [address, setAddress] = useState('');
  const [access, setAccess] = useState('');
  const [notes, setNotes] = useState('');
  const [currency, setCurrency] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api.updateApartment(apartment.slug, {
        name: name.trim(),
        city: city.trim(),
        country: country.trim().toUpperCase(),
        address: address.trim(),
        access: access.trim(),
        notes: notes.trim(),
        currency: currency.trim().toUpperCase(),
      }),
    onSuccess: () => {
      setEditing(false);
      void invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteApartment(apartment.slug),
    onSuccess: () => {
      onDeleted();
      void invalidate();
    },
  });
  const start = () => {
    setName(apartment.name);
    setCity(apartment.city ?? '');
    setCountry(apartment.country ?? '');
    setAddress(apartment.address ?? '');
    setAccess(apartment.access ?? '');
    setNotes(apartment.notes ?? '');
    setCurrency(apartment.currency ?? '');
    setEditing(true);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  if (editing) {
    return (
      <form className="card form form--wide" onSubmit={submit}>
        <div className="card__header">
          <h3>{apartment.name}</h3>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Удалить квартиру «${apartment.name}» вместе со всеми контактами, платежами и документами? Это навсегда и для всех.`,
                )
              ) {
                remove.mutate();
              }
            }}
          >
            Удалить квартиру
          </button>
        </div>
        <div className="form__row">
          <label>
            Название
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
            />
          </label>
          <label>
            Город
            <input value={city} onChange={(e) => setCity(e.target.value)} maxLength={200} />
          </label>
          <label>
            Страна
            <input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} />
          </label>
          <label>
            Валюта платежей
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
              placeholder="EUR"
            />
          </label>
        </div>
        <label>
          Адрес
          <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
        </label>
        <label>
          Доступ <span className="muted small">(коды, ключи, Wi-Fi; Markdown)</span>
          <textarea value={access} onChange={(e) => setAccess(e.target.value)} rows={5} />
        </label>
        <label>
          Заметки <span className="muted small">(Markdown)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={5} />
        </label>
        {save.isError && <p className="text-error small">{save.error.message}</p>}
        {remove.isError && <p className="text-error small">{remove.error.message}</p>}
        <FormButtons
          pending={save.isPending}
          ready={name.trim().length > 0}
          label="Сохранить"
          onCancel={() => setEditing(false)}
        />
      </form>
    );
  }

  return (
    <section className="card">
      <div className="card__header">
        <h3>{apartment.name}</h3>
        {canWrite && (
          <button type="button" className="btn btn--small btn--ghost" onClick={start}>
            Изменить
          </button>
        )}
      </div>
      <p className="muted small">
        {[apartment.city, apartment.country, apartment.currency].filter(Boolean).join(' · ')}
      </p>
      <p>{apartment.address ?? <span className="muted">Адрес не указан.</span>}</p>
      {apartment.access && (
        <div className="apartment__block">
          <h4>Доступ</h4>
          <div className="markdown">
            <Markdown remarkPlugins={[remarkGfm]}>{apartment.access}</Markdown>
          </div>
        </div>
      )}
      {apartment.notes && (
        <div className="apartment__block">
          <h4>Заметки</h4>
          <div className="markdown">
            <Markdown remarkPlugins={[remarkGfm]}>{apartment.notes}</Markdown>
          </div>
        </div>
      )}
    </section>
  );
}

/** Adds a payment, or changes one when `payment` is given. */
function PaymentForm({
  apartment,
  payment,
  onDone,
}: {
  apartment: ApartmentView;
  payment?: ApartmentPaymentView;
  onDone: () => void;
}) {
  const invalidate = useInvalidate();
  const [kind, setKind] = useState<ApartmentPaymentKind>(payment?.kind ?? 'utilities');
  const [title, setTitle] = useState(payment?.title ?? '');
  const [payee, setPayee] = useState(payment?.payee ?? '');
  const [amount, setAmount] = useState(payment?.amount ?? '');
  const [currency, setCurrency] = useState(payment?.currency ?? apartment.currency ?? 'EUR');
  const [dueDate, setDueDate] = useState(payment?.dueDate ?? today());
  const [paidDate, setPaidDate] = useState(payment?.paidDate ?? '');
  const [notes, setNotes] = useState(payment?.notes ?? '');
  const save = useMutation({
    mutationFn: () => {
      const fields = {
        kind,
        title: title.trim(),
        amount: Number(amount.replace(',', '.')),
        currency: currency.trim().toUpperCase(),
        dueDate,
      };
      return payment
        ? api.updateApartmentPayment(payment.id, {
            ...fields,
            payee: payee.trim(),
            notes: notes.trim(),
            paidDate: paidDate || null,
          })
        : api.addApartmentPayment(apartment.slug, {
            ...fields,
            ...(payee.trim() ? { payee: payee.trim() } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
            ...(paidDate ? { paidDate } : {}),
          });
    },
    onSuccess: () => {
      void invalidate();
      onDone();
    },
  });
  const ready =
    title.trim().length > 0 &&
    amount.trim().length > 0 &&
    !Number.isNaN(Number(amount.replace(',', '.')));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) save.mutate();
  };
  return (
    <form className="form form--wide apartment__form" onSubmit={submit}>
      <div className="form__row">
        <label>
          Название
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            placeholder="Свет, сентябрь"
          />
        </label>
        <label>
          Вид
          <select value={kind} onChange={(e) => setKind(e.target.value as ApartmentPaymentKind)}>
            {options(PAYMENT_LABELS)}
          </select>
        </label>
        <label>
          Получатель
          <input
            value={payee}
            onChange={(e) => setPayee(e.target.value)}
            maxLength={200}
            placeholder="Endesa"
          />
        </label>
      </div>
      <div className="form__row">
        <label>
          Сумма
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="84.30"
          />
        </label>
        <label>
          Валюта
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
        </label>
        <label>
          Дата счёта / срок
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
          />
        </label>
        <label>
          Оплачен <span className="muted small">(пусто — к оплате)</span>
          <input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
        </label>
      </div>
      <label>
        Заметки
        <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={8000} />
      </label>
      {save.isError && <p className="text-error small">{save.error.message}</p>}
      <FormButtons
        pending={save.isPending}
        ready={ready}
        label={payment ? 'Сохранить' : 'Добавить платёж'}
        onCancel={onDone}
      />
    </form>
  );
}

function PaymentRow({
  apartment,
  payment,
  canWrite,
}: {
  apartment: ApartmentView;
  payment: ApartmentPaymentView;
  canWrite: boolean;
}) {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const setPaid = useMutation({
    mutationFn: (paidDate: string | null) => api.setApartmentPaymentPaid(payment.id, paidDate),
    onSuccess: () => void invalidate(),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteApartmentPayment(payment.id),
    onSuccess: () => void invalidate(),
  });
  const error = setPaid.error ?? remove.error;
  if (editing) {
    return (
      <tr>
        <td colSpan={5}>
          <PaymentForm apartment={apartment} payment={payment} onDone={() => setEditing(false)} />
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="nowrap">{formatDay(payment.dueDate)}</td>
      <td>
        {payment.title}
        <div className="muted small">
          {[PAYMENT_LABELS[payment.kind], payment.payee].filter(Boolean).join(' · ')}
        </div>
        {payment.notes && <div className="small">{payment.notes}</div>}
        {error && <div className="text-error small">{error.message}</div>}
      </td>
      <td className="nowrap">{formatAmount(payment.amount, payment.currency)}</td>
      <td className="nowrap">
        {payment.paidDate ? (
          <span className="pill pill--ok">оплачен {formatDay(payment.paidDate)}</span>
        ) : (
          <span className="pill pill--error">к оплате</span>
        )}
      </td>
      {canWrite && (
        <td className="nowrap">
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={setPaid.isPending}
            onClick={() => setPaid.mutate(payment.paidDate ? null : today())}
          >
            {payment.paidDate ? 'Не оплачен' : 'Оплачен'}
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setEditing(true)}
          >
            Изменить
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить платёж «${payment.title}»?`)) remove.mutate();
            }}
          >
            Удалить
          </button>
        </td>
      )}
    </tr>
  );
}

function PaymentsSection({ apartment, canWrite }: { apartment: ApartmentView; canWrite: boolean }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="card">
      <div className="card__header">
        <h3>Платежи</h3>
        {canWrite && !adding && (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setAdding(true)}
          >
            Добавить
          </button>
        )}
      </div>
      {adding && <PaymentForm apartment={apartment} onDone={() => setAdding(false)} />}
      {apartment.payments.length === 0 ? (
        <p className="muted small">Платежей пока нет.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <tbody>
              {apartment.payments.map((p) => (
                <PaymentRow key={p.id} apartment={apartment} payment={p} canWrite={canWrite} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Adds a contact, or changes one when `contact` is given. */
function ContactForm({
  slug,
  contact,
  onDone,
}: {
  slug: string;
  contact?: ApartmentContactView;
  onDone: () => void;
}) {
  const invalidate = useInvalidate();
  const [role, setRole] = useState<ApartmentContactRole>(contact?.role ?? 'manager');
  const [name, setName] = useState(contact?.name ?? '');
  const [company, setCompany] = useState(contact?.company ?? '');
  const [phone, setPhone] = useState(contact?.phone ?? '');
  const [email, setEmail] = useState(contact?.email ?? '');
  const [notes, setNotes] = useState(contact?.notes ?? '');
  const save = useMutation({
    mutationFn: () =>
      contact
        ? api.updateApartmentContact(contact.id, {
            role,
            name: name.trim(),
            company: company.trim(),
            phone: phone.trim(),
            email: email.trim(),
            notes: notes.trim(),
          })
        : api.addApartmentContact(slug, {
            role,
            name: name.trim(),
            ...(company.trim() ? { company: company.trim() } : {}),
            ...(phone.trim() ? { phone: phone.trim() } : {}),
            ...(email.trim() ? { email: email.trim() } : {}),
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          }),
    onSuccess: () => {
      void invalidate();
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) save.mutate();
  };
  return (
    <form className="form form--wide apartment__form" onSubmit={submit}>
      <div className="form__row">
        <label>
          Имя
          <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
        </label>
        <label>
          Кто
          <select value={role} onChange={(e) => setRole(e.target.value as ApartmentContactRole)}>
            {options(ROLE_LABELS)}
          </select>
        </label>
      </div>
      <div className="form__row">
        <label>
          Компания
          <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={200} />
        </label>
        <label>
          Телефон
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={200}
            placeholder="+34 600 123 456"
          />
        </label>
        <label>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
      </div>
      <label>
        Заметки
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>
      {save.isError && <p className="text-error small">{save.error.message}</p>}
      <FormButtons
        pending={save.isPending}
        ready={name.trim().length > 0}
        label={contact ? 'Сохранить' : 'Добавить контакт'}
        onCancel={onDone}
      />
    </form>
  );
}

function ContactRow({
  slug,
  contact: c,
  canWrite,
}: {
  slug: string;
  contact: ApartmentContactView;
  canWrite: boolean;
}) {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const remove = useMutation({
    mutationFn: () => api.deleteApartmentContact(c.id),
    onSuccess: () => void invalidate(),
  });
  if (editing) {
    return (
      <li>
        <ContactForm slug={slug} contact={c} onDone={() => setEditing(false)} />
      </li>
    );
  }
  return (
    <li className="page-row">
      <div className="page-row__main">
        <span>
          <strong>{c.name}</strong>
          {c.company && ` · ${c.company}`}
        </span>
        <span className="muted small">{ROLE_LABELS[c.role]}</span>
        <span className="small">
          {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`}>{c.phone}</a>}
          {c.phone && c.email && ' · '}
          {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
        </span>
        {c.notes && <span className="small">{c.notes}</span>}
        {remove.isError && <span className="text-error small">{remove.error.message}</span>}
      </div>
      {canWrite && (
        <div className="actions-row">
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setEditing(true)}
          >
            Изменить
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Удалить контакт «${c.name}»?`)) remove.mutate();
            }}
          >
            Удалить
          </button>
        </div>
      )}
    </li>
  );
}

function ContactsSection({ apartment, canWrite }: { apartment: ApartmentView; canWrite: boolean }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="card">
      <div className="card__header">
        <h3>Контакты</h3>
        {canWrite && !adding && (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setAdding(true)}
          >
            Добавить
          </button>
        )}
      </div>
      {adding && <ContactForm slug={apartment.slug} onDone={() => setAdding(false)} />}
      {apartment.contactList.length === 0 ? (
        <p className="muted small">Контактов пока нет.</p>
      ) : (
        <ul className="page-list">
          {apartment.contactList.map((c) => (
            <ContactRow key={c.id} slug={apartment.slug} contact={c} canWrite={canWrite} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Adds a paper (with its file), or changes one when `document` is given. */
function DocumentForm({
  slug,
  document,
  filesSupported,
  onSaved,
  onCancel,
}: {
  slug: string;
  document?: ApartmentDocumentView;
  filesSupported: boolean;
  onSaved: (id: number) => void;
  onCancel: () => void;
}) {
  const invalidate = useInvalidate();
  const [kind, setKind] = useState<ApartmentDocumentKind>(document?.kind ?? 'contract');
  const [title, setTitle] = useState(document?.title ?? '');
  const [date, setDate] = useState(document?.date ?? today());
  const [issuer, setIssuer] = useState(document?.issuer ?? '');
  const [summary, setSummary] = useState(document?.summary ?? '');
  const [text, setText] = useState(document?.text ?? '');
  const [file, setFile] = useState<File | undefined>();
  const [fileWarning, setFileWarning] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: async () => {
      if (document) {
        await api.updateApartmentDocument(document.id, {
          kind,
          title: title.trim(),
          date,
          issuer: issuer.trim(),
          summary: summary.trim(),
          // An empty text is kept as it was unless the file stays alone on purpose.
          ...(text.trim() || document.file ? { text: text.trim() } : {}),
        });
        return document.id;
      }
      const { document: added } = await api.addApartmentDocument(slug, {
        kind,
        title: title.trim(),
        date,
        ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
        ...(summary.trim() ? { summary: summary.trim() } : {}),
        ...(text.trim() ? { text: text.trim() } : {}),
      });
      if (file) {
        try {
          await api.attachApartmentDocumentFile(added.id, file);
        } catch (err) {
          setFileWarning(
            `Документ сохранён, но файл не прикрепился: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      }
      return added.id;
    },
    onSuccess: (id) => {
      void invalidate();
      onSaved(id);
    },
  });
  const ready =
    title.trim().length > 0 &&
    (text.trim().length > 0 || file !== undefined || Boolean(document?.file));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFileWarning(undefined);
    if (ready) save.mutate();
  };
  return (
    <form className="form form--wide apartment__form" onSubmit={submit}>
      <div className="form__row">
        <label>
          Название
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
            placeholder="Договор на свет, Endesa"
          />
        </label>
        <label>
          Вид
          <select value={kind} onChange={(e) => setKind(e.target.value as ApartmentDocumentKind)}>
            {options(DOCUMENT_LABELS)}
          </select>
        </label>
      </div>
      <div className="form__row">
        <label>
          Дата документа
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
        </label>
        <label>
          Кем выдан
          <input value={issuer} onChange={(e) => setIssuer(e.target.value)} maxLength={200} />
        </label>
      </div>
      <label>
        Кратко <span className="muted small">(необязательно)</span>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={2}
          maxLength={8000}
        />
      </label>
      <label>
        Текст <span className="muted small">(Markdown; можно оставить пустым, если есть файл)</span>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} />
      </label>
      {filesSupported && !document && (
        <label>
          Файл оригинала <span className="muted small">(PDF, фото, до 50 МБ)</span>
          <input type="file" accept={FILE_ACCEPT} onChange={(e) => setFile(e.target.files?.[0])} />
        </label>
      )}
      {save.isError && <p className="text-error small">{save.error.message}</p>}
      {fileWarning && <p className="text-error small">{fileWarning}</p>}
      <FormButtons
        pending={save.isPending}
        ready={ready}
        label={document ? 'Сохранить' : 'Сохранить документ'}
        onCancel={onCancel}
      />
    </form>
  );
}

/** One paper with its text and file, opened from the list. */
function DocumentCard({
  id,
  slug,
  canWrite,
  filesSupported,
  onClose,
}: {
  id: number;
  slug: string;
  canWrite: boolean;
  filesSupported: boolean;
  onClose: () => void;
}) {
  const invalidate = useInvalidate();
  const [editing, setEditing] = useState(false);
  const query = useQuery({
    queryKey: ['apartments', 'document', id],
    queryFn: () => api.apartmentDocument(id),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteApartmentDocument(id),
    onSuccess: () => {
      onClose();
      void invalidate();
    },
  });
  const attach = useMutation({
    mutationFn: (file: File) => api.attachApartmentDocumentFile(id, file),
    onSuccess: () => void invalidate(),
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const d = query.data?.document;
  if (editing && d) {
    return (
      <article className="card document">
        <div className="card__header">
          <h3>{d.title}</h3>
        </div>
        <DocumentForm
          slug={slug}
          document={d}
          filesSupported={filesSupported}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      </article>
    );
  }
  return (
    <article className="card document">
      <div className="card__header">
        <h3>{d?.title ?? 'Документ'}</h3>
        <div className="actions-row">
          {canWrite && d && filesSupported && (
            <>
              <input
                ref={fileInput}
                type="file"
                hidden
                accept={FILE_ACCEPT}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) attach.mutate(file);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="btn btn--small btn--ghost"
                disabled={attach.isPending}
                onClick={() => fileInput.current?.click()}
              >
                {attach.isPending ? 'Загружаю…' : d.file ? 'Заменить файл' : 'Прикрепить файл'}
              </button>
            </>
          )}
          {canWrite && d && (
            <>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                onClick={() => setEditing(true)}
              >
                Изменить
              </button>
              <button
                type="button"
                className="btn btn--small btn--ghost"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Удалить «${d.title}»? Это навсегда и для всех.`)) {
                    remove.mutate();
                  }
                }}
              >
                Удалить
              </button>
            </>
          )}
          <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {remove.isError && <p className="text-error small">{remove.error.message}</p>}
      {attach.isError && <p className="text-error small">{attach.error.message}</p>}
      {d && (
        <>
          <p className="muted small">
            {[DOCUMENT_LABELS[d.kind], formatDay(d.date), d.issuer].filter(Boolean).join(' · ')}
          </p>
          {d.file && (
            <p className="document__file">
              <a href={api.apartmentDocumentFileUrl(d.id)} target="_blank" rel="noreferrer">
                {d.file.name}
              </a>
            </p>
          )}
          {d.summary && (
            <p className="document__summary">
              <strong>Кратко.</strong> {d.summary}
            </p>
          )}
          {d.text ? (
            <div className="document__text markdown">
              <Markdown remarkPlugins={[remarkGfm]}>{d.text}</Markdown>
            </div>
          ) : (
            <p className="muted small">Текста нет: только файл.</p>
          )}
        </>
      )}
    </article>
  );
}

function DocumentsSection({
  apartment,
  canWrite,
  filesSupported,
}: {
  apartment: ApartmentView;
  canWrite: boolean;
  filesSupported: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<number | undefined>();
  return (
    <section className="card">
      <div className="card__header">
        <h3>Документы</h3>
        {canWrite && !adding && (
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => setAdding(true)}
          >
            Добавить
          </button>
        )}
      </div>
      {adding && (
        <DocumentForm
          slug={apartment.slug}
          filesSupported={filesSupported}
          onSaved={(id) => {
            setAdding(false);
            setOpen(id);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {open !== undefined && (
        <DocumentCard
          key={open}
          id={open}
          slug={apartment.slug}
          canWrite={canWrite}
          filesSupported={filesSupported}
          onClose={() => setOpen(undefined)}
        />
      )}
      {apartment.documentList.length === 0 ? (
        <p className="muted small">Документов пока нет.</p>
      ) : (
        <ul className="page-list">
          {apartment.documentList.map((d) => (
            <li key={d.id} className="page-row">
              <div className="page-row__main">
                <button
                  type="button"
                  className="link-button page-row__title"
                  onClick={() => setOpen(d.id)}
                >
                  {d.title}
                </button>
                <span className="muted small">
                  {[DOCUMENT_LABELS[d.kind], formatDay(d.date), d.issuer]
                    .filter(Boolean)
                    .join(' · ')}
                  {d.file && ' · 📎'}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const UTILITY_LABELS: Record<UtilityBillView['utility'], string> = {
  electricity: 'Свет',
  gas: 'Газ',
  water: 'Вода',
  internet: 'Интернет',
};

const fixed = (value: number | string | null, digits: number) =>
  value === null ? '—' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: digits });

/** The parts of one bill, as the supplier printed them. */
function BillBreakdown({ bill }: { bill: UtilityBillView }) {
  const lines: [string, string | null][] = [
    ['Мощность', bill.powerCost],
    ['Энергия', bill.energyCost],
    ['Регулируемые сборы', bill.regulatedCharges],
    ['Налог на электричество', bill.electricityTax],
    ['Аренда счётчика', bill.meterRental],
    ...bill.extras.map((e): [string, string] => [e.label, e.amount]),
    ['Без НДС', bill.subtotal],
    [`НДС${bill.vatRate ? ` ${fixed(bill.vatRate, 2)} %` : ''}`, bill.vat],
    ['Итого', bill.total],
  ];
  return (
    <div className="bill__breakdown small">
      <p className="muted">
        {[
          bill.product,
          bill.accessTariff,
          bill.reading === 'estimated'
            ? 'показания оценочные'
            : bill.reading && 'показания реальные',
          bill.invoiceNumber && `№ ${bill.invoiceNumber}`,
          bill.supplyPoint && `CUPS ${bill.supplyPoint}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {(bill.consumptionP1 ?? bill.powerP1) && (
        <p>
          {bill.consumptionP1 &&
            `P1 ${fixed(bill.consumptionP1, 2)} · P2 ${fixed(bill.consumptionP2, 2)} · P3 ${fixed(bill.consumptionP3, 2)} кВт·ч`}
          {bill.powerP1 && ` · мощность ${fixed(bill.powerP1, 2)} / ${fixed(bill.powerP2, 2)} кВт`}
          {bill.powerPriceP1 &&
            ` по ${fixed(bill.powerPriceP1, 6)} / ${fixed(bill.powerPriceP2, 6)} за кВт в день`}
        </p>
      )}
      <table className="table">
        <tbody>
          {lines
            .filter(([, v]) => v !== null)
            .map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="nowrap">{formatAmount(value ?? '0', bill.currency)}</td>
              </tr>
            ))}
        </tbody>
      </table>
      {bill.documentId && (
        <a href={api.apartmentDocumentFileUrl(bill.documentId)} target="_blank" rel="noreferrer">
          Оригинал счёта
        </a>
      )}
    </div>
  );
}

/**
 * Utility bills: compared by supplier (per kWh with everything included and per day, since
 * periods differ), then bill by bill with its breakdown.
 */
function BillsSection({ slug, canWrite }: { slug: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['apartments', 'bills', slug],
    queryFn: () => api.utilityBills(slug),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.deleteUtilityBill(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['apartments'] }),
  });
  const data = query.data;
  if (!data || data.bills.length === 0) {
    return (
      <section className="card">
        <div className="card__header">
          <h3>Счета за коммуналку</h3>
        </div>
        {query.isError && <p className="text-error small">{query.error.message}</p>}
        <p className="muted small">
          Счетов пока нет. Пришлите агенту PDF счёта за свет, газ или воду — он разберёт его по
          статьям.
        </p>
      </section>
    );
  }
  return (
    <section className="card">
      <div className="card__header">
        <h3>Счета за коммуналку</h3>
      </div>
      <h4>По компаниям</h4>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Компания</th>
              <th>Счетов</th>
              <th>Дней</th>
              <th>Расход в день</th>
              <th>Цена за кВт·ч, всё включено</th>
              <th>В день</th>
              <th>Тариф: энергия / мощность P1, P2</th>
            </tr>
          </thead>
          <tbody>
            {data.byProvider.map((p) => (
              <tr key={`${p.utility}-${p.provider}-${p.currency}`}>
                <td>
                  {p.provider}
                  <div className="muted small">{UTILITY_LABELS[p.utility]}</div>
                </td>
                <td>{p.bills}</td>
                <td>{p.days}</td>
                <td className="nowrap">{fixed(p.consumptionPerDay, 2)}</td>
                <td className="nowrap">
                  {p.totalPerUnit === null ? '—' : `${fixed(p.totalPerUnit, 4)} ${p.currency}`}
                </td>
                <td className="nowrap">{formatAmount(String(p.totalPerDay), p.currency)}</td>
                <td className="nowrap small">
                  {fixed(p.latest.energyPrice, 6)} / {fixed(p.latest.powerPriceP1, 6)},{' '}
                  {fixed(p.latest.powerPriceP2, 6)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4>Счета</h4>
      {remove.isError && <p className="text-error small">{remove.error.message}</p>}
      <ul className="page-list">
        {data.bills.map((b) => (
          <li key={b.id} className="bill">
            <details>
              <summary>
                <strong>
                  {formatDay(b.periodStart)} – {formatDay(b.periodEnd)}
                </strong>{' '}
                · {b.provider} · {b.days} дн. ·{' '}
                {b.consumption !== null && `${fixed(b.consumption, 2)} кВт·ч · `}
                <strong>{formatAmount(b.total, b.currency)}</strong>
                <span className="muted small">
                  {b.metrics.totalPerUnit !== null &&
                    ` · ${fixed(b.metrics.totalPerUnit, 4)} за кВт·ч`}
                  {` · ${fixed(b.metrics.totalPerDay, 2)} в день`}
                </span>
              </summary>
              <BillBreakdown bill={b} />
              {canWrite && (
                <button
                  type="button"
                  className="btn btn--small btn--ghost"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm('Удалить счёт? Платёж и документ останутся.')) {
                      remove.mutate(b.id);
                    }
                  }}
                >
                  Удалить счёт
                </button>
              )}
            </details>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The Overview card: what is still to pay, all apartments together. */
export function UnpaidPaymentsCard() {
  const query = useQuery({
    queryKey: ['apartments', 'unpaid'],
    queryFn: () => api.unpaidApartmentPayments(),
  });
  const names = useQuery({ queryKey: ['apartments', 'list'], queryFn: () => api.apartments() });
  const nameOf = new Map((names.data?.apartments ?? []).map((a) => [a.slug, a.name]));
  const data = query.data;
  if (!data && !query.isError) return null;
  const totals = Object.entries(data?.unpaidTotals ?? {});
  return (
    <article className="card">
      <header className="card__header">
        <h3>Квартиры: к оплате</h3>
        <Link to="/apartments" className="small">
          Все квартиры
        </Link>
      </header>
      {query.isError && <p className="text-error small">{query.error.message}</p>}
      {data && data.payments.length === 0 && <p className="muted">Всё оплачено.</p>}
      {data && data.payments.length > 0 && (
        <>
          <p>
            <strong>{totals.map(([cur, sum]) => formatAmount(sum, cur)).join(' + ')}</strong>
            <span className="muted small"> · счетов: {data.total}</span>
          </p>
          <ul className="event-list">
            {data.payments.slice(0, 8).map((p) => (
              <li key={p.id} className="event-row">
                <span className="event-row__time">{formatDay(p.dueDate)}</span>
                <span className="event-row__title">
                  {p.title} · {formatAmount(p.amount, p.currency)}
                  <span className="muted small"> · {nameOf.get(p.apartment) ?? p.apartment}</span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </article>
  );
}

/**
 * Apartments: one tab each, with the address and access, payments, contacts and papers.
 * Everyone reads; changing needs the right `apartments:write` (the owner gives it in
 * Settings → Users) or the owner role.
 */
export function Apartments() {
  const list = useQuery({ queryKey: ['apartments', 'list'], queryFn: () => api.apartments() });
  const items = list.data?.apartments ?? [];
  const [chosen, setSelected] = useState<string | undefined>();
  const [adding, setAdding] = useState(false);
  const selected = items.some((a) => a.slug === chosen) ? chosen : items[0]?.slug;
  const one = useQuery({
    queryKey: ['apartments', 'one', selected],
    queryFn: () => api.apartment(selected ?? ''),
    enabled: selected !== undefined,
  });
  const canWrite = list.data?.canWrite ?? false;
  const filesSupported = list.data?.filesSupported ?? false;
  const apartment = one.data?.apartment;

  return (
    <div className="page">
      <div className="page__header">
        <h2>Квартиры</h2>
        {canWrite && !adding && (
          <button
            type="button"
            className="btn btn--small btn--primary"
            onClick={() => setAdding(true)}
          >
            Добавить квартиру
          </button>
        )}
      </div>
      {adding && (
        <AddApartmentForm
          onAdded={(slug) => {
            setAdding(false);
            setSelected(slug);
          }}
          onCancel={() => setAdding(false)}
        />
      )}
      {list.isPending && <p className="muted">Загрузка…</p>}
      {list.isError && <p className="text-error">{list.error.message}</p>}
      {list.data && !list.data.available && <p className="muted">Квартиры недоступны: нет базы.</p>}
      {list.data?.available && items.length === 0 && <p className="muted">Квартир пока нет.</p>}
      {items.length > 0 && (
        <div className="tabs" role="tablist" aria-label="Квартиры">
          {items.map((a) => (
            <button
              key={a.slug}
              type="button"
              role="tab"
              aria-selected={a.slug === selected}
              className={a.slug === selected ? 'active' : undefined}
              onClick={() => setSelected(a.slug)}
            >
              {a.name}
              {a.unpaid > 0 && <span className="tabs__count">{a.unpaid}</span>}
            </button>
          ))}
        </div>
      )}
      {one.isError && <p className="text-error">{one.error.message}</p>}
      {apartment && (
        <div className="apartment" key={apartment.slug}>
          <InfoSection
            apartment={apartment}
            canWrite={canWrite}
            onDeleted={() => setSelected(undefined)}
          />
          <PaymentsSection apartment={apartment} canWrite={canWrite} />
          <BillsSection slug={apartment.slug} canWrite={canWrite} />
          <ContactsSection apartment={apartment} canWrite={canWrite} />
          <DocumentsSection
            apartment={apartment}
            canWrite={canWrite}
            filesSupported={filesSupported}
          />
        </div>
      )}
    </div>
  );
}
